import { useMemo, useState } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { InputField, SelectField, SwitchField } from 'components/form';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { AddEditDeclineReasonDialogFormValues } from 'modules/creditSubmission/types';
import { getAddEditDeclineReasonCategoryOptions } from 'modules/creditSubmission/lib';
import _ from 'lodash';
import { keyBy } from 'utils/arrayUtils';
import { UPSERT_CREDIT_SUBMISSION_DECLINE_REASONS } from 'modules/creditSubmission/api/upsertCreditSubmissionDeclineReasons';
import { useSelector } from 'react-redux';

export const AddEditDeclineReasonDialog = () => {
  const {
    isOpen,
    handleClose,
    data: { creditSubmissionDeclineReasons, refetchCreditSubmissionDeclineReasons },
  } = useModal(ModalsKeys.AddEditDeclineReasonDialog);

  const { showNotification } = useNotifications();

  const [upsertCreditSubmissionDeclineReasons] = useMutation(UPSERT_CREDIT_SUBMISSION_DECLINE_REASONS);

  const account = useSelector((state: any) => state.account);

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<AddEditDeclineReasonDialogFormValues>({
    defaultValues: {
      newCategory: '',
      newDeclineReason: '',
      creditSubmissionDeclineReasonsById: keyBy(creditSubmissionDeclineReasons, 'creditSubmissionDeclineReasonId'),
    },
  });

  const handleSave = async (formData: AddEditDeclineReasonDialogFormValues) => {
    try {
      setIsLoading(true);
      const existingReasons = Object.values(formData.creditSubmissionDeclineReasonsById);
      const reasonsToSave =
        formData.newCategory && formData.newDeclineReason
          ? [...existingReasons, { category: formData.newCategory, label: formData.newDeclineReason, isActive: true }]
          : existingReasons;
      const input = reasonsToSave.map(csdr => {
        return {
          // If there is no accountId, it indicates that the creditSubmissionDeclineReason the default one. In this case, we need to create a new one specific to that account
          creditSubmissionDeclineReasonId: csdr.accountId ? csdr.creditSubmissionDeclineReasonId : null,
          accountId: account.id,
          category: csdr.category,
          label: csdr.label,
          isActive: csdr.isActive,
        };
      });
      await upsertCreditSubmissionDeclineReasons({ variables: { input } });
      await refetchCreditSubmissionDeclineReasons();
      handleClose();
      showNotification('Decline Reasons saved');
    } catch (error) {
      showNotification('Error', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const { categoryOptions, groupedDeclineReasons } = useMemo(() => {
    return {
      categoryOptions: getAddEditDeclineReasonCategoryOptions(),
      groupedDeclineReasons: _.groupBy(creditSubmissionDeclineReasons, 'category'),
    };
  }, []);

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="span" variant="h6">
          Edit Decline Reason
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap="1rem">
          <Typography variant="h6" component="p">
            Add New
          </Typography>
          <SelectField name="newCategory" label="Category" control={form.control} options={categoryOptions} />
          <InputField control={form.control} label="Decline Reason" name="newDeclineReason" />
        </Stack>

        <Typography variant="h6" component="p" mt={3} mb={2}>
          Created Decline Reasons
        </Typography>
        <Stack gap="1rem" divider={<Divider />}>
          {Object.keys(groupedDeclineReasons).map(category => (
            <Box key={category}>
              <Typography variant="subtitle2" component="span">
                {category}
              </Typography>
              {groupedDeclineReasons[category].map(csdr => (
                <Box key={csdr.label} display="flex" alignItems="center">
                  <SwitchField
                    control={form.control}
                    name={`creditSubmissionDeclineReasonsById.${csdr.creditSubmissionDeclineReasonId}.isActive`}
                  />
                  <Typography variant="body2">{csdr.label}</Typography>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    sx={{
                      color:
                        form.watch(
                          `creditSubmissionDeclineReasonsById.${csdr.creditSubmissionDeclineReasonId}.isActive`
                        ) === true
                          ? '#4CAF50'
                          : '#FF5252',
                      marginLeft: 'auto',
                    }}
                  >
                    {form.watch(
                      `creditSubmissionDeclineReasonsById.${csdr.creditSubmissionDeclineReasonId}.isActive`
                    ) === true
                      ? 'Active'
                      : 'Inactive'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={form.handleSubmit(handleSave)} variant="contained" loading={isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
